import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import {
  CloudDone as SyncedIcon,
  CloudOff as OfflineIcon,
  CloudUpload as WaitingIcon,
  Sync as SyncIcon,
  WarningAmber as ProblemIcon,
} from '@mui/icons-material';
import { useNetwork } from '../../context/NetworkContext';
import { useSync } from '../../context/SyncContext';
import IconAction from '../common/IconAction';

const changes = (n) => `${n} change${n === 1 ? '' : 's'}`;

const timeLabel = (at) => {
  if (!at) return null;
  const date = new Date(at);
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return date.toDateString() === new Date().toDateString()
    ? `today at ${time}`
    : `${date.toLocaleDateString([], { day: 'numeric', month: 'short' })} at ${time}`;
};

const STATUS_LABEL = {
  conflict: 'Changed on the server',
  not_found: 'Deleted on the server',
  rejected: 'Not accepted',
  forbidden: 'Not allowed',
  blocked: 'Not sent',
};

const fieldErrors = (errors) =>
  errors && typeof errors === 'object'
    ? Object.entries(errors)
        .map(([field, messages]) => `${field.replace(/_/g, ' ')}: ${[].concat(messages).join(', ')}`)
        .join('; ')
    : null;

const summary = ({ offline, syncing, pending, problems, error }) => {
  if (syncing) return { icon: <CircularProgress size={20} />, text: `Syncing ${changes(pending)}…` };
  const isOffline = offline || error === 'offline';
  let text;
  if (isOffline) text = pending ? `Offline · ${changes(pending)} saved on this device` : 'Offline';
  else if (pending) text = `${changes(pending)} waiting to sync`;
  // Problems are mentioned alongside, never instead of, changes still waiting.
  if (problems.length) text = [text, `${changes(problems.length)} not applied`].filter(Boolean).join(' · ');
  const icon = problems.length ? <ProblemIcon color="warning" />
    : isOffline ? <OfflineIcon color="action" />
      : pending ? <WaitingIcon color="primary" />
        : <SyncedIcon color="success" />;
  return { icon, text: text || 'All changes synced' };
};

/**
 * Navbar indicator for the outbox: synced / waiting / offline / problems, with
 * a panel to sync now and review changes the server didn't apply.
 */
const SyncStatus = () => {
  const { offline } = useNetwork();
  const sync = useSync();
  const [anchor, setAnchor] = useState(null);
  const { icon, text } = summary({ offline, ...sync });
  const badge = sync.problems.length || sync.pending;

  return (
    <>
      <IconAction
        label={text}
        ariaLabel={`Sync status: ${text}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ mr: 0.5 }}
        icon={
          <Badge badgeContent={badge} color={sync.problems.length ? 'warning' : 'primary'} max={999}>
            {icon}
          </Badge>
        }
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxWidth: 'calc(100vw - 32px)' } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2">{text}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {sync.pending
              ? "Changes are kept on this device until the server has them, even if the app is closed."
              : sync.lastSyncedAt
                ? `Last synced ${timeLabel(sync.lastSyncedAt)}.`
                : 'Changes you make are saved on this device and sent to the server automatically.'}
          </Typography>
          {sync.error && sync.error !== 'offline' && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>{sync.error}</Typography>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<SyncIcon />}
            onClick={() => sync.syncNow()}
            // Not disabled when we think we're offline: that's exactly when
            // someone wants to try again once signal is back.
            disabled={sync.syncing || !sync.pending}
            sx={{ mt: 1.5 }}
          >
            Sync now
          </Button>
        </Box>
        {sync.problems.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 2, maxHeight: 360, overflowY: 'auto' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Not applied</Typography>
                <Button size="small" onClick={() => sync.dismiss(sync.problems.map((p) => p.id))}>Dismiss all</Button>
              </Stack>
              <Stack spacing={1}>
                {sync.problems.map((problem) => (
                  <Paper key={problem.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 600 }}>
                      {STATUS_LABEL[problem.status] || problem.status} · {problem.op === 'create' ? 'new' : problem.op === 'delete' ? 'deletion' : 'edit'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{problem.what}</Typography>
                    <Typography variant="body2" color="text.secondary">{problem.message}</Typography>
                    {fieldErrors(problem.errors) && (
                      <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>{fieldErrors(problem.errors)}</Typography>
                    )}
                    <Button size="small" onClick={() => sync.dismiss([problem.id])} sx={{ mt: 0.5, ml: -1 }}>Dismiss</Button>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default SyncStatus;
